// The `*.protein-features.json` artifact contract (written by
// `omnitool.protein_features` in the omnitool-py sandbox) and its mapping onto
// the data shapes Nightingale's components take.

const SCHEMA = "protein-features/1";

export interface ProteinFeature {
  start: number;
  end: number;
  label: string;
  color: string | null;
}

export interface ProteinFeatureTrack {
  label: string;
  features: ProteinFeature[];
}

export interface ProteinVariant {
  position: number;
  ref: string;
  alt: string;
  count: number;
  consequence: string;
  label: string;
}

export interface ProteinFeatures {
  protein: { accession: string; name: string | null; sequence: string };
  tracks: ProteinFeatureTrack[];
  variants: ProteinVariant[];
}

export interface NightingaleTrackFeature {
  accession: string;
  start: number;
  end: number;
  color?: string;
  shape?: "circle";
  tooltipContent: string;
}

export interface NightingaleVariationDatum {
  accession: string;
  start: number;
  variant: string;
  wildType: string;
  consequenceType: string;
  xrefNames: string[];
  hasPredictions: boolean;
  tooltipContent: string;
}

export interface NightingaleData {
  length: number;
  sequence: string;
  tracks: { label: string; features: NightingaleTrackFeature[] }[];
  variantPositions: NightingaleTrackFeature[];
  variation: { sequence: string; variants: NightingaleVariationDatum[] };
}

export function parseProteinFeatures(raw: unknown): ProteinFeatures {
  const doc = asRecord(raw, "document");
  if (doc.schema !== SCHEMA) {
    throw new Error(`Unsupported protein-features schema: ${String(doc.schema)}`);
  }
  const protein = asRecord(doc.protein, "protein");
  return {
    protein: {
      accession: asString(protein.accession, "protein.accession"),
      name: protein.name == null ? null : asString(protein.name, "protein.name"),
      sequence: asString(protein.sequence, "protein.sequence"),
    },
    tracks: asArray(doc.tracks, "tracks").map((t, i) => {
      const track = asRecord(t, `tracks[${i}]`);
      return {
        label: asString(track.label, `tracks[${i}].label`),
        features: asArray(track.features, `tracks[${i}].features`).map((f, j) => {
          const where = `tracks[${i}].features[${j}]`;
          const feature = asRecord(f, where);
          return {
            start: asNumber(feature.start, `${where}.start`),
            end: asNumber(feature.end, `${where}.end`),
            label: asString(feature.label, `${where}.label`),
            color: feature.color == null ? null : asString(feature.color, `${where}.color`),
          };
        }),
      };
    }),
    variants: asArray(doc.variants, "variants").map((v, i) => {
      const where = `variants[${i}]`;
      const variant = asRecord(v, where);
      return {
        position: asNumber(variant.position, `${where}.position`),
        ref: asString(variant.ref, `${where}.ref`),
        alt: asString(variant.alt, `${where}.alt`),
        count: asNumber(variant.count, `${where}.count`),
        consequence: asString(variant.consequence, `${where}.consequence`),
        label: asString(variant.label, `${where}.label`),
      };
    }),
  };
}

export function toNightingaleData(doc: ProteinFeatures): NightingaleData {
  const byPosition = new Map<number, ProteinVariant[]>();
  for (const variant of doc.variants) {
    byPosition.set(variant.position, [...(byPosition.get(variant.position) ?? []), variant]);
  }

  return {
    length: doc.protein.sequence.length,
    sequence: doc.protein.sequence,
    tracks: doc.tracks.map((track) => ({
      label: track.label,
      features: track.features.map((feature, i) => ({
        accession: `${track.label}-${i}`,
        start: feature.start,
        end: feature.end,
        color: feature.color ?? undefined,
        tooltipContent: feature.label,
      })),
    })),
    variantPositions: [...byPosition.entries()]
      .sort(([a], [b]) => a - b)
      .map(([position, variants]) => ({
        accession: `pos-${position}`,
        start: position,
        end: position,
        shape: "circle",
        tooltipContent: variants.map(variantSummary).join(", "),
      })),
    variation: {
      sequence: doc.protein.sequence,
      variants: doc.variants.map((variant) => ({
        accession: variant.label,
        start: variant.position,
        variant: variant.alt,
        wildType: variant.ref,
        consequenceType: variant.consequence,
        xrefNames: [],
        hasPredictions: false,
        tooltipContent: variantSummary(variant),
      })),
    },
  };
}

function variantSummary(variant: ProteinVariant): string {
  return `${variant.label} ×${variant.count}`;
}

function asRecord(value: unknown, where: string): Record<string, unknown> {
  if (value === null || typeof value !== "object" || Array.isArray(value)) {
    throw new Error(`Invalid protein-features document: ${where} must be an object`);
  }
  return value as Record<string, unknown>;
}

function asArray(value: unknown, where: string): unknown[] {
  if (!Array.isArray(value)) {
    throw new Error(`Invalid protein-features document: ${where} must be an array`);
  }
  return value;
}

function asString(value: unknown, where: string): string {
  if (typeof value !== "string" || !value) {
    throw new Error(`Invalid protein-features document: ${where} must be a non-empty string`);
  }
  return value;
}

function asNumber(value: unknown, where: string): number {
  if (typeof value !== "number" || !Number.isFinite(value)) {
    throw new Error(`Invalid protein-features document: ${where} must be a number`);
  }
  return value;
}
